import { createServerFn } from "@tanstack/react-start";
import * as db from "@/lib/db.server";
import { requireAuth } from "@/lib/auth/middleware";

/**
 * Server fns for the public provider page. Split out of the route file so the
 * route module does not statically import db.server / auth middleware at top
 * level (which keeps the `.middleware([requireAuth])` reference alive in the
 * eager client bundle and trips TanStack import-protection). Imported only by
 * the code-split route module → resolved into the server bundle.
 */

// ---- Server functions (Aurora-backed) ---------------------------------------

// PUBLIC: resolve a provider row by its short public_id (used by the loader).
// Direct SELECT bypasses the DB function's stale return-type cache so npi is included.
export const getProviderByPublicIdFn = createServerFn({ method: "POST" })
  .inputValidator((d: { publicId: string }) => d)
  .handler(async ({ data }) => {
    const row = await db.one(
      `SELECT * FROM public.providers
       WHERE public_id = $1
         AND is_active = true AND is_deleted = false AND moderation_status = 'approved'
       LIMIT 1`,
      [data.publicId],
    );
    return row;
  });

// PUBLIC: resolve a provider row by a UUID prefix (used by the loader).
export const getProviderByIdPrefixFn = createServerFn({ method: "POST" })
  .inputValidator((d: { prefix: string }) => d)
  .handler(async ({ data }) => {
    const row = await db.one(
      `SELECT * FROM public.providers
       WHERE id::text LIKE lower($1) || '%'
         AND is_active = true AND is_deleted = false AND moderation_status = 'approved'
       LIMIT 1`,
      [data.prefix],
    );
    return row;
  });

// PUBLIC: published weekly availability windows for a provider's booking calendar.
export const loadProviderAvailabilityFn = createServerFn({ method: "GET" })
  .inputValidator((d: { providerId: string }) => d)
  .handler(async ({ data }) => {
    const rows = await db.query(
      `SELECT day_of_week, start_time, end_time, slot_duration_minutes, is_active
       FROM public.provider_availability
       WHERE provider_id = $1 AND is_active = true`,
      [data.providerId],
    );
    return rows;
  });

// PUBLIC: fire-and-forget social-link click analytics for a provider page.
export const trackSocialClickFn = createServerFn({ method: "POST" })
  .inputValidator((d: { providerId: string; platform: string }) => d)
  .handler(async ({ data }) => {
    await db.execute(
      "INSERT INTO public.social_clicks (provider_id, platform) VALUES ($1, $2)",
      [data.providerId, data.platform],
    );
    return { ok: true };
  });

// AUTH: persist a patient booking. The patient (owner) is taken from the
// authenticated session, never from the client.
export const createBookingFn = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .inputValidator((d: {
    providerId: string;
    customerName: string;
    customerPhone: string | null;
    serviceRequested: string;
    appointmentAtISO: string;
    notes?: string | null;
  }) => d)
  .handler(async ({ data, context }) => {
    // Owner scoping: the appointments table keys the patient by email (no
    // user-id column migrated). Use the authenticated session email, never a
    // client-supplied value.
    const inserted = await db.one(
      `INSERT INTO public.appointments
         (listing_id, customer_name, customer_email, customer_phone,
          service_requested, appointment_at, status, source, notes)
       VALUES ($1, $2, $3, $4, $5, $6, 'booked', 'web', $7)
       RETURNING id`,
      [
        data.providerId,
        data.customerName,
        context.user.email,
        data.customerPhone,
        data.serviceRequested,
        data.appointmentAtISO,
        data.notes ?? null,
      ],
    );
    return { id: inserted?.id ?? null };
  });
